import React from "react";
import { ArrowRight, Users, Award, Compass, FolderCode, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { academyData } from "@/data/mock";

const featureIcons = [Users, Award, Compass, FolderCode];

export default function Academy() {
  return (
    <div className="min-h-screen bg-slate-900">
      {/* Hero Section */}
      <section className="pt-32 pb-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-slate-950 to-slate-900" />
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-teal-500/10 rounded-full blur-3xl" />
        
        <div className="container mx-auto px-6 lg:px-12 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <Badge className="bg-teal-500/10 text-teal-400 border-teal-500/20 mb-6 px-4 py-1.5">
              Professional Training
            </Badge>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              {academyData.headline}
            </h1>
            <p className="text-xl text-slate-400 leading-relaxed mb-10">
              {academyData.subheadline}
            </p>
            <a href="https://academy.botverseweb.com" target="_blank" rel="noopener noreferrer">
              <Button size="lg" className="bg-teal-500 hover:bg-teal-600 text-slate-900 font-semibold px-8 group">
                Visit Academy
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-slate-950">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-white mb-4">What Makes Us Different</h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Practical, job-ready skills taught by industry practitioners
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {academyData.features.map((feature, index) => {
              const Icon = featureIcons[index];
              return (
                <Card key={index} className="bg-slate-900/50 border-slate-800 hover:border-teal-500/30 transition-colors">
                  <CardContent className="p-6 text-center">
                    <div className="w-14 h-14 rounded-2xl bg-teal-500/10 flex items-center justify-center mx-auto mb-4">
                      <Icon className="w-7 h-7 text-teal-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-white mb-2">{feature.title}</h3>
                    <p className="text-slate-400 text-sm">{feature.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Learning Tracks */}
      <section className="py-20 bg-slate-900">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl font-bold text-white mb-6">Learning Tracks</h2>
              <p className="text-slate-400 mb-8 leading-relaxed">
                Our curriculum is designed in collaboration with industry partners to ensure you learn skills that employers actually need.
              </p>
              <div className="space-y-4">
                {academyData.tracks.map((track, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-teal-400" />
                    <span className="text-white">{track}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-teal-500/10 to-cyan-500/10 rounded-3xl blur-xl" />
              <div className="relative bg-slate-800/50 border border-slate-700 rounded-2xl p-8">
                <h3 className="text-2xl font-bold text-white mb-4">Ready to Level Up?</h3>
                <p className="text-slate-400 mb-6">
                  Join thousands of professionals who have transformed their careers through our programs.
                </p>
                <div className="space-y-3 mb-8">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-400">Live cohort-based learning</span>
                    <CheckCircle className="w-4 h-4 text-teal-400" />
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-400">Industry certifications</span>
                    <CheckCircle className="w-4 h-4 text-teal-400" />
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-400">Career support</span>
                    <CheckCircle className="w-4 h-4 text-teal-400" />
                  </div>
                </div>
                <a href="https://academy.botverseweb.com" target="_blank" rel="noopener noreferrer" className="block">
                  <Button className="w-full bg-teal-500 hover:bg-teal-600 text-slate-900 font-semibold group">
                    Explore Programs
                    <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Note Section */}
      <section className="py-12 bg-slate-950 border-t border-slate-800">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-slate-500 text-sm">
              Botverse Academy operates as a dedicated learning platform. Visit{" "}
              <a href="https://academy.botverseweb.com" className="text-teal-400 hover:underline" target="_blank" rel="noopener noreferrer">
                academy.botverseweb.com
              </a>{" "}
              for course details, schedules, and enrollment.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
